import React from 'react';
import {
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import {
  useCodeAgentTurnMetrics,
  type CodeAgentTurnMetricsRow,
  type CodeAgentTurnMetricsTotals,
} from '../../services/code-agent.service';
import { formatDateTime } from '../../utils/dateFormat';

type Props = {
  sessionPublicId: string | null;
  onClose: () => void;
};

const num = (metrics: Record<string, unknown> | null, key: string): number => {
  const value = metrics?.[key];
  return typeof value === 'number' && Number.isFinite(value) ? value : 0;
};

const ratio = (value: number | null | undefined): string =>
  typeof value === 'number' ? `${Math.round(value * 100)}%` : '—';

const retryBreakdown = (metrics: Record<string, unknown> | null): string => {
  const retry = metrics?.retry_count;
  if (!retry || typeof retry !== 'object') return '—';
  const entries = Object.entries(retry as Record<string, number>).filter(([, v]) => v > 0);
  return entries.length ? entries.map(([k, v]) => `${k}:${v}`).join(' ') : '0';
};

function TotalsRow({ totals }: { totals: CodeAgentTurnMetricsTotals | null }) {
  if (!totals) return null;
  const items: Array<[string, string]> = [
    ['Turns', `${totals.turnsWithMetrics}/${totals.turns}`],
    ['Iterations', String(totals.iterations)],
    ['Prompt tokens', totals.promptTokens.toLocaleString()],
    ['Completion tokens', totals.completionTokens.toLocaleString()],
    ['Cache read', totals.cacheReadTokens.toLocaleString()],
    ['Cache write', totals.cacheWriteTokens.toLocaleString()],
    ['Cache hit', ratio(totals.cacheReadRatio)],
    ['Retries', String(totals.retryTotal)],
  ];
  return (
    <Box className="mb-4 flex flex-wrap gap-2">
      {items.map(([label, value]) => (
        <Chip key={label} size="small" variant="outlined" label={`${label}: ${value}`} />
      ))}
    </Box>
  );
}

function MetricsTable({ rows }: { rows: CodeAgentTurnMetricsRow[] }) {
  return (
    <Table size="small">
      <TableHead>
        <TableRow>
          <TableCell>Turn</TableCell>
          <TableCell>State</TableCell>
          <TableCell align="right">Iter</TableCell>
          <TableCell align="right">Prompt</TableCell>
          <TableCell align="right">Completion</TableCell>
          <TableCell align="right">Cache read</TableCell>
          <TableCell align="right">Cache write</TableCell>
          <TableCell align="right">Hit</TableCell>
          <TableCell>Retries</TableCell>
          <TableCell align="right">Compact</TableCell>
          <TableCell align="right">Embed</TableCell>
          <TableCell align="right">Router</TableCell>
          <TableCell align="right">Duration</TableCell>
        </TableRow>
      </TableHead>
      <TableBody>
        {rows.map((row) => {
          const m = row.metrics;
          const duration = num(m, 'duration_ms');
          return (
            <TableRow key={row.turnId}>
              <TableCell>
                <Typography variant="caption" className="font-mono">
                  {row.turnId.slice(0, 12)}
                </Typography>
                <Typography variant="caption" className="block text-gray-500">
                  {formatDateTime(row.createdAt)}
                </Typography>
              </TableCell>
              <TableCell>
                <Chip size="small" label={row.state} variant="outlined" />
              </TableCell>
              <TableCell align="right">{m ? num(m, 'iterations') : '—'}</TableCell>
              <TableCell align="right">
                {m ? num(m, 'prompt_tokens').toLocaleString() : '—'}
              </TableCell>
              <TableCell align="right">
                {m ? num(m, 'completion_tokens').toLocaleString() : '—'}
              </TableCell>
              <TableCell align="right">
                {m ? num(m, 'cache_read_tokens').toLocaleString() : '—'}
              </TableCell>
              <TableCell align="right">
                {m ? num(m, 'cache_write_tokens').toLocaleString() : '—'}
              </TableCell>
              <TableCell align="right">
                {ratio(m?.cache_read_ratio as number | null | undefined)}
              </TableCell>
              <TableCell>
                <Typography variant="caption">{retryBreakdown(m)}</Typography>
              </TableCell>
              <TableCell align="right">{m ? num(m, 'compaction_count') : '—'}</TableCell>
              <TableCell align="right">{m ? num(m, 'embedding_calls') : '—'}</TableCell>
              <TableCell align="right">{m ? num(m, 'router_calls') : '—'}</TableCell>
              <TableCell align="right">{m ? `${(duration / 1000).toFixed(1)}s` : '—'}</TableCell>
            </TableRow>
          );
        })}
      </TableBody>
    </Table>
  );
}

/** Spec 01 §0: per-turn token / cache / retry metrics of one Code Agent session. */
export function CodeAgentTurnMetricsDialog({ sessionPublicId, onClose }: Props) {
  const { data, isLoading, error } = useCodeAgentTurnMetrics(sessionPublicId);

  return (
    <Dialog open={sessionPublicId !== null} onClose={onClose} maxWidth="xl" fullWidth>
      <DialogTitle>Turn metrics</DialogTitle>
      <DialogContent dividers>
        {isLoading ? (
          <Box className="flex items-center justify-center p-8">
            <CircularProgress />
          </Box>
        ) : error ? (
          <Typography className="text-red-600">Failed to load turn metrics.</Typography>
        ) : !data || data.rows.length === 0 ? (
          <Typography variant="body2" className="text-gray-600">
            No turns recorded for this session yet.
          </Typography>
        ) : (
          <>
            <TotalsRow totals={data.totals} />
            <MetricsTable rows={data.rows} />
          </>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Close</Button>
      </DialogActions>
    </Dialog>
  );
}
